export default class DCFAnalysis {
  constructor({
    exitYear = 10,
    yearsFormatted = [],
    longTermGrowthRate = 0,
    baseFCF = 0,
    earnoutYears = 0,
    earnoutComp = 0,
    consultingMonths = 0,
    consultingFee = 0,
    synergiesType = "Fixed",
    fixedSynergiesRate = 0,
    variableSynergiesRateSeries = yearsFormatted.map(() => 0),
    manualSynergiesRateMatrix = [{
      name: "",
      series: yearsFormatted.map(() => 0),
    }],
  }) {
    this.exitYear = exitYear;
    this.yearsFormatted = yearsFormatted;
    this.longTermGrowthRate = longTermGrowthRate;
    this.baseFCF = baseFCF;
    this.earnoutYears = earnoutYears;
    this.earnoutComp = earnoutComp;
    this.consultingMonths = consultingMonths;
    this.consultingFee = consultingFee;
    this.synergiesType = synergiesType;
    this.fixedSynergiesRate = fixedSynergiesRate;
    this.variableSynergiesRateSeries = variableSynergiesRateSeries;
    this.manualSynergiesRateMatrix = manualSynergiesRateMatrix;
  }

  get FCFtoFirm() {
    const { yearsFormatted, baseFCF, longTermGrowthRate } = this;

    return yearsFormatted.map((year, index) => ({
      date: year,
      value: baseFCF * Math.pow((1 + longTermGrowthRate / 100), index + 1),
    }))
  }

  get synergiesAndGrowth() {
    const {
      synergiesType,
      FCFtoFirm,
      fixedSynergiesRate,
      variableSynergiesRateSeries,
      manualSynergiesRateMatrix
    } = this;

    if(synergiesType === "Fixed")
      return FCFtoFirm.map((obj) => ({
        date: obj.date,
        value: obj.value * fixedSynergiesRate / 100,
      }))
    else if(synergiesType === "Variable")
      return FCFtoFirm.map((obj, index) => ({
        date: obj.date,
        value: obj.value * variableSynergiesRateSeries[index] / 100,
      }))
    else if(synergiesType === "Manual")
      return FCFtoFirm.map((obj, index) => ({
        date: obj.date,
        value: manualSynergiesRateMatrix.reduce((acc, obj) => acc + obj.series[index], 0),
      }))
  }

  get totalFCF() {
    const { FCFtoFirm, synergiesAndGrowth } = this;
    return this.getArraySum(FCFtoFirm, synergiesAndGrowth);
  }

  get earnoutArray() {
    const { yearsFormatted, earnoutYears, earnoutComp } = this;
    return yearsFormatted.map((year, index) => {
      if(index < earnoutYears) return {
        date: year,
        value: earnoutComp,
        disabled: false,
      }
      else return {
        date: year,
        value: 0,
        disabled: true,
      };
    });
  }

  get consultingArray() {
    const { yearsFormatted, consultingMonths, consultingFee } = this;
    return yearsFormatted.map((year, index) => {
      if(index < consultingMonths / 12) return {
        date: year,
        value: index < Math.floor(consultingMonths / 12)
          ? consultingFee * 12
          : consultingFee * (consultingMonths % 12),
        disabled: false,
      }
      else return {
        date: year,
        value: 0,
        disabled: true,
      };
    });
  }

  get totalDebtTerm() {
    const { earnoutArray, consultingArray } = this;
    return this.getArraySum(earnoutArray, consultingArray);
  }

  get FCFAvailable() {
    const { totalFCF, totalDebtTerm } = this;
    return this.getArraySum(totalFCF, totalDebtTerm, { minus: true });
  }

  getArraySum(array1, array2, options = { minus: false }) {
    //add element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        ({
          date: year,
          value: options.minus ?
            Number(array1[index].value) - Number(array2[index].value) :
            Number(array1[index].value) + Number(array2[index].value),
          disabled: (array1[index].disabled && array2[index].disabled) || false,
        })
    );
  }
}
